import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Container, Grid, Header, Segment, Card, Icon, Statistic, Image, Button, Label, List, Divider, Loader, Message, Table } from 'semantic-ui-react';
import { AuthStdFooter } from "../../utils/auth-std-footer";
import { getMenuIdForFeature } from '../../utils/roleUtils';
import { fetchActorApplications, selectActorApplications, selectApplicationsStatus } from '../../slices/applicationSlice';
import {
  fetchPrimaryPortfolio,
  fetchSampleCharacters,
  selectPrimaryPortfolio,
  selectSampleCharacters,
  selectLoadingStates as selectSampleLoadingStates
} from '../../slices/sampleCharacterSlice';
import { format } from 'date-fns';
import '../../styles/landing-pages.css';
import '../../styles/article.css';
import bookStack from '../../assets/justBookStack.svg';
import hourGlass from '../../assets/hourGlassLine.svg';
import checkBox from '../../assets/checkListLine.svg';
import voiceActressBW from '../../assets/micAndshield.svg';
import myProfileLine from '../../assets/myProfileLine.svg';
import lightening from '../../assets/lightening.svg';
import headAndMicPhones from '../../assets/headAndMicPhones.svg';
import profileIcon from '../../assets/profileLineGlasses.svg'

const VoiceActorLandingPage = () => {
  const dispatch = useDispatch();
  const userProfile = useSelector((state) => state.user.profile);
  const userName = userProfile?.data?.title || 'Voice Actor';

  // Get current user's actorId for applications
  const currentUser = useSelector(state => state.user.profile?.data);
  const actorId = currentUser?.userId;

  // Get applications from Redux
  const applications = useSelector(selectActorApplications(actorId || ''));
  const appStatus = useSelector(selectApplicationsStatus);

  // Get sample characters from Redux
  const primaryPortfolio = useSelector(selectPrimaryPortfolio);
  const sampleCharacters = useSelector(selectSampleCharacters);
  const sampleLoading = useSelector(selectSampleLoadingStates);

  // Format date helper (defined early so memos can use it)
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return format(new Date(dateStr), 'MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  // Filter for accepted assignments only (for the quick view)
  const acceptedAssignments = useMemo(
    () => applications.filter(app => app.status === 'Accepted').slice(0, 5),
    [applications]
  );

  // Derive recent activity from applications (sorted by most recent, top 5)
  const recentActivity = useMemo(() => {
    if (!applications || applications.length === 0) return [];

    const statusConfig = {
      'Accepted': { icon: 'check circle', color: 'green', verb: 'Application accepted for' },
      'Rejected': { icon: 'times circle', color: 'red', verb: 'Application declined for' },
      'Declined': { icon: 'times circle', color: 'red', verb: 'Application declined for' },
      'pending': { icon: 'clock outline', color: 'blue', verb: 'Applied for' },
      'Pending': { icon: 'clock outline', color: 'blue', verb: 'Applied for' },
      'submitted': { icon: 'paper plane', color: 'blue', verb: 'Submitted application for' },
      'Submitted': { icon: 'paper plane', color: 'blue', verb: 'Submitted application for' },
      'under_review': { icon: 'eye', color: 'orange', verb: 'Under review:' },
      'in_review': { icon: 'eye', color: 'orange', verb: 'Under review:' },
    };

    return [...applications]
      .sort((a, b) => {
        const dateA = new Date(a.updatedAt || a.submissionDate || a.createdAt || 0);
        const dateB = new Date(b.updatedAt || b.submissionDate || b.createdAt || 0);
        return dateB - dateA;
      })
      .slice(0, 5)
      .map(app => {
        const config = statusConfig[app.status] || { icon: 'info circle', color: 'grey', verb: 'Activity for' };
        const title = app.opportunity?.title || app.opportunityTitle || 'an opportunity';
        const date = app.updatedAt || app.submissionDate || app.createdAt;
        return {
          uuid: app.uuid,
          icon: config.icon,
          color: config.color,
          text: `${config.verb} "${title}"`,
          date: date ? formatDate(date) : null,
          status: app.status
        };
      });
  }, [applications]);

  // Fetch applications and portfolio on component mount
  useEffect(() => {
    if (actorId) {
      dispatch(fetchActorApplications(actorId));
      dispatch(fetchPrimaryPortfolio(actorId));
    }
  }, [dispatch, actorId]);

  // Fetch sample characters when portfolio is loaded
  useEffect(() => {
    if (primaryPortfolio?.uuid) {
      dispatch(fetchSampleCharacters(primaryPortfolio.uuid));
    }
  }, [dispatch, primaryPortfolio?.uuid]);

  // Quick action cards for voice actors
  const quickActions = [
    {
      icon: 'shopping bag',
      img: bookStack,
      title: 'Opportunity Marketplace',
      description: 'Browse and apply for voice acting opportunities',
      link: '/opportunities'
    },
    {
      icon: 'tasks',
      img: hourGlass,
      title: 'My Applications & Assignments',
      description: 'Track your applications and active assignments',
      link: '/dashboard',
      state: { menuSelection: getMenuIdForFeature('myAssignments') }
    },
    // {
    //   icon: 'microphone',
    //   img: voiceActressBW,
    //   title: 'Voice Lab',
    //   description: 'Record and manage your voice samples',
    //   link: '/dashboard',
    //   state: { menuSelection: getMenuIdForFeature('voiceLab') }
    // },
    {
      icon: 'folder',
      img: bookStack,
      title: 'My Portfolio',
      description: 'Manage/Create your voice actor portfolio',
      link: '/dashboard',
      state: { menuSelection: getMenuIdForFeature('myPortfolio') }
    },
    // ARCHIVED: My Recordings card removed — legacy Voice Lab page
    // {
    //   icon: 'file audio',
    //   img: headAndMicPhones,
    //   title: 'My Recordings',
    //   description: 'Browse your recorded voice samples',
    //   link: '/dashboard',
    //   state: { menuSelection: getMenuIdForFeature('voiceLabMyRecordings') }
    // }
  ];

  // Derive voice actor statistics from client-side data (applications + sampleCharacters)
  const derivedStats = useMemo(() => {
    const apps = applications || [];
    const assignmentCount = apps.filter(a => a.status === 'Accepted').length;
    const completedCount = apps.filter(a =>
      ['completed', 'Completed'].includes(a.status)
    ).length;
    const characterCount = sampleCharacters.length;
    const withAudioCount = sampleCharacters.filter(c => c.hasAudio || c.audioUrl).length;

    return { assignmentCount, completedCount, characterCount, withAudioCount };
  }, [applications, sampleCharacters]);

  const statsLoading = appStatus === 'loading' || sampleLoading?.characters;

  const statistics = [
    {
      label: 'Assignments',
      value: statsLoading ? '...' : String(derivedStats.assignmentCount),
      icon: 'tasks',
      img: hourGlass,
      link: '/dashboard',
      state: { menuSelection: getMenuIdForFeature('myAssignments') }
    },
    {
      label: 'Completed',
      value: statsLoading ? '...' : String(derivedStats.completedCount),
      icon: 'check circle',
      img: checkBox,
      link: '/dashboard',
      state: { menuSelection: getMenuIdForFeature('myAssignments') }
    },
    {
      label: 'Characters',
      value: statsLoading ? '...' : String(derivedStats.characterCount),
      icon: 'user',
      img: bookStack,
      link: '/dashboard',
      state: { menuSelection: getMenuIdForFeature('myPortfolio') }
    },
    {
      label: 'Recordings',
      value: statsLoading ? '...' : String(derivedStats.withAudioCount),
      icon: 'file audio',
      img: headAndMicPhones,
      link: '/dashboard',
      state: { menuSelection: getMenuIdForFeature('myAssignments') }
    },
    {
      label: 'My Profile',
      value: '',
      icon: 'user',
      img: profileIcon,
      link: '/profile'
    }
  ];

  return (
    <Container fluid className="landing-page voice-actor-landing">
      <Container style={{ marginTop: "30px" }}>
        <Grid className="articleListWidth">
          <Grid.Row>
            <Grid.Column width={3}>
              <div className="img-with-gradient">
                <Image src="/static/media/shutterstock_2294366549-1024x1024.3f3f1276513c895ca1e0.png" />
              </div>
            </Grid.Column>
            <Grid.Column width={13} textAlign="center">
              <span className="blgListItemTitle">Welcome, {userName}</span>
              <span className="blgListSubTitle">Voice Actor Dashboard</span>
              <p className="landing-subheading-text">
                As a Voice Actor, you can manage your assignments, record voice samples, and build your portfolio.
                Use the quick actions below to navigate to common tasks.
              </p>
              <div style={{ textAlign: 'left', marginTop: '1em' }}>
                <Button
                  as={Link}
                  to="/dashboard"
                  size="large"
                  className="landing-goto-button"
                >
                  Go to Main Dashboard
                </Button>
              </div>
            </Grid.Column>
          </Grid.Row>
        </Grid>
        <Grid className="headerBottomLine" style={{ marginTop: "20px", borderTop: "solid 1px #AAAAAA" }}></Grid>
      </Container>

      <Container style={{ marginTop: '2em', marginBottom: '2em' }}>
        <Grid>
          <Grid.Row>
            <Grid.Column width={6}>
              <Header as="h2">
                <img src={myProfileLine} alt="" />
                <Header.Content style={{
                  fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
                  fontWeight: '400',
                  fontSize: '2.5rem'
                }}>
                  Your Performance
                  <Header.Subheader>Overview of your voice acting work</Header.Subheader>
                </Header.Content>
              </Header>
            </Grid.Column>
            <Grid.Column width={10}>
              <div>
                <Statistic.Group widths="six">
                  {statistics.map((stat, index) => {
                    const StatisticContent = (
                      <>
                        <Statistic.Value>
                          <div style={{
                            fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
                            fontWeight: '300',
                            fontSize: '3.0rem'
                          }}><img src={stat.img} alt="" /> {stat.value}</div>
                        </Statistic.Value>
                        <Statistic.Label style={{
                          fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
                          fontWeight: '300',
                          fontSize: '1.1rem'
                        }}>{stat.label}</Statistic.Label>
                      </>
                    );

                    // If link is provided, wrap with Link component
                    if (stat.link) {
                      return (
                        <Statistic
                          key={index}
                          as={Link}
                          to={stat.link}
                          state={stat.state}
                          className="statistic-link"
                        >
                          {StatisticContent}
                        </Statistic>
                      );
                    }

                    // Otherwise, render as a regular Statistic
                    return (
                      <Statistic key={index}>
                        {StatisticContent}
                      </Statistic>
                    );
                  })}
                </Statistic.Group>
              </div>
            </Grid.Column>
          </Grid.Row>
        </Grid>
        <Divider />

        <Header as="h2" dividing style={{ marginTop: '2em' }}>
          <img src={lightening} alt="" />
          <Header.Content>
            Quick Actions
            <Header.Subheader>Common voice actor tasks</Header.Subheader>
          </Header.Content>
        </Header>

        <Card.Group itemsPerRow={5} stackable>
          {quickActions.map((action, index) => (
            <Card
              key={index}
              as={Link}
              to={action.link}
              state={action.state}
              raised
            >
              <Card.Content>
                <img src={action.img} alt="" style={{ height: '80px', display: 'block', margin: '0 auto 1em', color: '#4a6b8a' }} />
                <Card.Header textAlign="center">{action.title}</Card.Header>
                <Card.Description textAlign="center">
                  {action.description}
                </Card.Description>
              </Card.Content>
            </Card>
          ))}
        </Card.Group>

        <Grid columns={2} stackable style={{ marginTop: '2em' }}>
          <Grid.Column>
            <Header as="h2" dividing>
              <img src={hourGlass} alt="" style={{ height: '50px' }} />
              <Header.Content>
                Current Assignments
                <Header.Subheader>Your active projects</Header.Subheader>
              </Header.Content>
            </Header>

            <Segment style={{ padding: '0.5em' }}>
              {appStatus === 'loading' && (
                <div style={{ textAlign: 'center', padding: '2em' }}>
                  <Loader active inline="centered" size="small" />
                  <p style={{ marginTop: '0.5em', color: '#888' }}>Loading assignments...</p>
                </div>
              )}

              {appStatus !== 'loading' && acceptedAssignments.length === 0 && (
                <Message info style={{ margin: '0.5em' }}>
                  <Message.Header>No Active Assignments</Message.Header>
                  <p>You don't have any accepted assignments yet. Browse the <Link to="/opportunities">Opportunity Marketplace</Link> to find voice acting roles.</p>
                </Message>
              )}

              {appStatus !== 'loading' && acceptedAssignments.length > 0 && (
                <Table compact="very" basic="very" size="small" unstackable style={{ margin: 0 }}>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>Opportunity</Table.HeaderCell>
                      <Table.HeaderCell>Character</Table.HeaderCell>
                      <Table.HeaderCell>Date</Table.HeaderCell>
                      <Table.HeaderCell textAlign="center">Status</Table.HeaderCell>
                      <Table.HeaderCell textAlign="center"></Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {acceptedAssignments.map((assignment, index) => (
                      <Table.Row key={assignment.uuid || index}>
                        <Table.Cell>
                          <strong style={{ fontSize: '0.92em' }}>
                            {assignment.opportunity?.title || 'Untitled'}
                          </strong>
                          {assignment.book && (
                            <div style={{ fontSize: '0.82em', color: '#666', marginTop: '2px' }}>
                              <Icon name="book" size="small" />
                              {assignment.book?.name || assignment.book?.title || '—'}
                              {assignment.litGroup?.name && (
                                <span style={{ color: '#999' }}> · {assignment.litGroup.name}</span>
                              )}
                            </div>
                          )}
                        </Table.Cell>
                        <Table.Cell style={{ fontSize: '0.9em' }}>
                          {assignment.character?.name || '—'}
                        </Table.Cell>
                        <Table.Cell style={{ fontSize: '0.85em', whiteSpace: 'nowrap', color: '#555' }}>
                          {formatDate(assignment.submissionDate || assignment.createdAt)}
                        </Table.Cell>
                        <Table.Cell textAlign="center">
                          <Label color="green" size="mini">
                            <Icon name="check" /> Accepted
                          </Label>
                        </Table.Cell>
                        <Table.Cell textAlign="center">
                          <Button
                            as={Link}
                            to={`/applications/${assignment.uuid}`}
                            compact
                            size="mini"
                            basic
                          >
                            Details
                          </Button>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              )}

              <div style={{ textAlign: 'center', marginTop: '0.75em', paddingBottom: '0.5em' }}>
                <Button
                  as={Link}
                  to="/dashboard"
                  state={{ menuSelection: getMenuIdForFeature('myAssignments') }}
                  compact
                  size="small"
                >
                  View All Applications & Assignments
                </Button>
              </div>
            </Segment>
          </Grid.Column>

          <Grid.Column>
            <Header as="h2" dividing>
              <img src={bookStack} alt="" style={{ height: '50px' }} />
              <Header.Content>
                Your Portfolio
                <Header.Subheader>Voice samples and characters</Header.Subheader>
              </Header.Content>
            </Header>

            <Segment>
              <Header as="h4">
                Sample Characters
                <Label circular color="blue" size="mini" style={{ marginLeft: '8px' }}>
                  {sampleLoading?.characters ? '...' : sampleCharacters.length}
                </Label>
              </Header>
              {sampleLoading?.characters ? (
                <div style={{ textAlign: 'center', padding: '1em' }}>
                  <Loader active inline="centered" size="mini" />
                </div>
              ) : sampleCharacters.length === 0 ? (
                <Message size="small" info>
                  <p>No sample characters yet. Create samples to showcase your voice talent.</p>
                </Message>
              ) : (
                <List divided relaxed={false} size="small">
                  {sampleCharacters.slice(0, 5).map((char) => (
                    <List.Item key={char.uuid} style={{ padding: '0.4em 0' }}>
                      <Icon name="user" color="blue" />
                      <List.Content>
                        <strong>{char.name || 'Unnamed'}</strong>
                        {char.characterType && (
                          <span style={{ color: '#888', marginLeft: '0.5em', fontSize: '0.85em' }}>
                            ({char.characterType})
                          </span>
                        )}
                        {char.hasAudio && (
                          <Icon name="microphone" color="green" size="small" style={{ marginLeft: '0.5em' }} />
                        )}
                      </List.Content>
                    </List.Item>
                  ))}
                  {sampleCharacters.length > 5 && (
                    <List.Item style={{ padding: '0.4em 0', color: '#888', fontSize: '0.85em' }}>
                      <Icon name="ellipsis horizontal" />
                      <List.Content>+{sampleCharacters.length - 5} more</List.Content>
                    </List.Item>
                  )}
                </List>
              )}
              <div style={{ textAlign: 'center', marginTop: '1em' }}>
                <Button
                  as={Link}
                  to="/dashboard"
                  state={{ menuSelection: getMenuIdForFeature('myPortfolio') }}
                  compact
                  size="small"
                >
                  Manage Portfolio
                </Button>
              </div>
            </Segment>

            <Segment>
              <Header as="h4">Recent Activity</Header>
              {appStatus === 'loading' ? (
                <div style={{ textAlign: 'center', padding: '1em' }}>
                  <Loader active inline="centered" size="mini" />
                </div>
              ) : recentActivity.length === 0 ? (
                <Message size="small" info>
                  <p>No recent activity. Browse the <Link to="/opportunities">marketplace</Link> to get started.</p>
                </Message>
              ) : (
                <List divided relaxed={false} size="small">
                  {recentActivity.map((activity, index) => (
                    <List.Item key={activity.uuid || index} style={{ padding: '0.5em 0' }}>
                      <Icon name={activity.icon} color={activity.color} />
                      <List.Content>
                        <span style={{ fontSize: '0.9em' }}>{activity.text}</span>
                        {activity.date && (
                          <span style={{ color: '#999', fontSize: '0.8em', marginLeft: '0.5em' }}>
                            {activity.date}
                          </span>
                        )}
                      </List.Content>
                    </List.Item>
                  ))}
                </List>
              )}
              <div style={{ textAlign: 'center', marginTop: '1em' }}>
                <Button
                  as={Link}
                  to="/dashboard"
                  state={{ menuSelection: getMenuIdForFeature('myAssignments') }}
                  fluid
                  size="small"
                  style={{ backgroundColor: '#4a6b8a', color: 'white' }}
                >
                  <Icon name="microphone" /> View Assignments & Record
                </Button>
              </div>
            </Segment>
          </Grid.Column>
        </Grid>
      </Container>

      {AuthStdFooter()}
    </Container>
  );
};

export default VoiceActorLandingPage;
